import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import cx from 'classnames';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import playSVG from '@plone/volto/icons/play.svg';
import stopSVG from '@plone/volto/icons/clear.svg';

import speedSVG from '../../../icons/speed.svg';
import {
  isSpeechSupported,
  ensureVoicesLoaded,
  configurePortugueseUtterance,
  createReadingChunks,
  type ReadingChunk,
} from '../../../utils/speechSynthesis';

const STORAGE_KEYS = {
  rate: 'accessibility:main-reader-rate',
};

const RATE_MIN = 0.6;
const RATE_MAX = 1.4;
const DEFAULT_RATE = 1;
const messages = defineMessages({
  playLabel: {
    id: 'accessibilityMainReaderPlay',
    defaultMessage: 'Ouvir conteúdo',
  },
  stopLabel: {
    id: 'accessibilityMainReaderStop',
    defaultMessage: 'Parar',
  },

  speedLabel: {
    id: 'accessibilityMainReaderSpeed',
    defaultMessage: 'Velocidade da leitura do conteúdo principal',
  },
});

const getStoredNumber = (key: string, fallback: number): number => {
  if (typeof window === 'undefined') return fallback;
  try {
    const value = Number(window.localStorage.getItem(key));
    return Number.isFinite(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

const savePreference = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures.
  }
};

const getMainContentNode = (): HTMLElement | null => {
  if (typeof document === 'undefined') return null;
  return (
    document.querySelector<HTMLElement>('main') ||
    document.querySelector<HTMLElement>('#main') ||
    document.querySelector<HTMLElement>('.view-wrapper') ||
    document.querySelector<HTMLElement>('.document-view')
  );
};

interface Props {
  align?: 'left' | 'center' | 'right' | 'full';
  className?: string;
  style?: React.CSSProperties;
}

const DefaultView: React.FC<Props> = ({ align, className, style }) => {
  const intl = useIntl();
  const chunksRef = useRef<ReadingChunk[]>([]);
  const chunkIndexRef = useRef(0);
  const betweenChunksTimeoutRef = useRef<number | null>(null);
  const isStartedRef = useRef(false);
  const rateRef = useRef(DEFAULT_RATE);

  const [speechSupported, setSpeechSupported] = useState(false);
  const [rate, setRate] = useState(DEFAULT_RATE);
  const [isReading, setIsReading] = useState(false);
  const safeAlign =
    align && ['left', 'center', 'right', 'full'].includes(align)
      ? align
      : 'center';
  // layout/float is handled by SCSS classes (align-left / align-right / align-center / align-full)

  const ratePercent = useMemo(() => Math.round(rate * 100), [rate]);

  /**
   * Stops all speech and cleans up state
   */
  const stopReading = useCallback(() => {
    if (!isSpeechSupported()) return;

    window.speechSynthesis.cancel();

    if (betweenChunksTimeoutRef.current) {
      window.clearTimeout(betweenChunksTimeoutRef.current);
      betweenChunksTimeoutRef.current = null;
    }

    chunksRef.current = [];
    chunkIndexRef.current = 0;
    isStartedRef.current = false;
    setIsReading(false);
  }, []);

  /**
   * Speaks the current chunk and schedules the next one
   */
  const speakCurrentChunk = useCallback(() => {
    if (!isSpeechSupported() || !isStartedRef.current) return;

    const nextChunk = chunksRef.current[chunkIndexRef.current];
    if (!nextChunk) {
      stopReading();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(nextChunk.text);
    configurePortugueseUtterance(utterance, rateRef.current);

    utterance.onend = () => {
      if (!isStartedRef.current) return;
      chunkIndexRef.current += 1;
      const pauseAfterMs = nextChunk.pauseAfterMs;
      if (pauseAfterMs > 0) {
        betweenChunksTimeoutRef.current = window.setTimeout(() => {
          speakCurrentChunk();
        }, pauseAfterMs);
        return;
      }

      speakCurrentChunk();
    };

    utterance.onerror = () => {
      if (!isStartedRef.current) return;
      stopReading();
    };

    window.speechSynthesis.speak(utterance);
  }, [stopReading]);

  /**
   * Starts reading the main content
   */
  const startReading = useCallback(() => {
    // Prevent multiple simultaneous reads
    if (isStartedRef.current || isReading) return;
    if (!speechSupported) return;

    const mainNode = getMainContentNode();
    if (!mainNode) return;

    const chunks = createReadingChunks(mainNode);
    if (!chunks.length) return;

    // Cancel any existing speech
    window.speechSynthesis.cancel();

    // Initialize state
    chunksRef.current = chunks;
    chunkIndexRef.current = 0;
    isStartedRef.current = true;
    setIsReading(true);

    // Start speaking
    speakCurrentChunk();
  }, [isReading, speechSupported, speakCurrentChunk]);

  /**
   * Handle rate change - reinicia utterance atual com nova velocidade
   */
  const onChangeRate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextRate = Number(event.target.value);
    rateRef.current = nextRate;
    setRate(nextRate);
    savePreference(STORAGE_KEYS.rate, String(nextRate));

    if (isReading && isStartedRef.current) {
      window.speechSynthesis.cancel();

      if (betweenChunksTimeoutRef.current) {
        window.clearTimeout(betweenChunksTimeoutRef.current);
        betweenChunksTimeoutRef.current = null;
      }

      chunksRef.current = [];
      chunkIndexRef.current = 0;
      isStartedRef.current = false;
      setIsReading(false);
    }
  };

  /**
   * Initialize: Load voices and set initial rate
   */
  useEffect(() => {
    const supported = isSpeechSupported();
    setSpeechSupported(supported);
    if (!supported) return;

    const initialRate = Math.min(
      RATE_MAX,
      Math.max(RATE_MIN, getStoredNumber(STORAGE_KEYS.rate, DEFAULT_RATE)),
    );

    rateRef.current = initialRate;
    setRate(initialRate);

    // Ensure voices are loaded
    return ensureVoicesLoaded();
  }, []);

  /**
   * Cleanup: Stop speaking when component unmounts or when page changes
   */
  useEffect(() => {
    return () => {
      if (betweenChunksTimeoutRef.current) {
        window.clearTimeout(betweenChunksTimeoutRef.current);
      }
      isStartedRef.current = false;
      chunksRef.current = [];
      chunkIndexRef.current = 0;
      if (isSpeechSupported()) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  return (
    <div
      className={cx(
        'block align main-content-reader-block',
        { center: safeAlign === 'center' },
        safeAlign,
        className,
      )}
      style={style}
    >
      <figure className={cx('figure', safeAlign)}>
        <div className="main-content-reader">
          <div className="main-content-reader__controls">
            <button
              type="button"
              className="main-content-reader__button"
              onClick={startReading}
              disabled={!speechSupported || isReading}
              aria-label={intl.formatMessage(messages.playLabel)}
              title={intl.formatMessage(messages.playLabel)}
            >
              <Icon name={playSVG} size="32px" />
              <p>{intl.formatMessage(messages.playLabel)}</p>
            </button>

            <button
              type="button"
              className={cx('main-content-reader__button', {
                'is-active': isReading,
              })}
              onClick={stopReading}
              disabled={!speechSupported || !isReading}
              aria-label={intl.formatMessage(messages.stopLabel)}
              title={intl.formatMessage(messages.stopLabel)}
            >
              <Icon name={stopSVG} size="32px" />
              <p>{intl.formatMessage(messages.stopLabel)}</p>
            </button>
          </div>

          <label className="main-content-reader__range">
            <span className="main-content-reader__icon" aria-hidden="true">
              <Icon name={speedSVG} size="48px" style={{ fill: 'unset' }} />
            </span>
            <input
              type="range"
              min={RATE_MIN}
              max={RATE_MAX}
              step="0.1"
              value={rate}
              onChange={onChangeRate}
              disabled={!speechSupported}
              aria-label={intl.formatMessage(messages.speedLabel)}
            />
            <span aria-hidden="true">{ratePercent}%</span>
          </label>
        </div>
      </figure>
    </div>
  );
};

export default DefaultView;
